import type { UserPlatformProfile } from "types/interfaces/user";

export type RoleProfile = UserPlatformProfile | null | undefined;

/** Archived users keep no capabilities. */
function live(p: RoleProfile): UserPlatformProfile | undefined {
  return p && !p.is_archived ? p : undefined;
}

export const isSuperAdmin = (p: RoleProfile): boolean =>
  live(p)?.is_superuser === true;

export const isManager = (p: RoleProfile): boolean =>
  live(p)?.is_calibrate_manager === true;

export const isPlatformAdmin = (p: RoleProfile): boolean =>
  isSuperAdmin(p) || isManager(p);

export const isPartnershipTeam = (p: RoleProfile): boolean =>
  live(p)?.is_partnership_team === true;

export const isDeveloper = (p: RoleProfile): boolean =>
  live(p)?.is_developer === true;

export const isRegularInternalUser = (p: RoleProfile): boolean =>
  live(p)?.user_type === "internal" &&
  !isSuperAdmin(p) &&
  !isManager(p) &&
  !isPartnershipTeam(p);

export const isInternalLike = (p: RoleProfile): boolean =>
  isSuperAdmin(p) ||
  isManager(p) ||
  isRegularInternalUser(p) ||
  isPartnershipTeam(p);

export const isClientUser = (p: RoleProfile): boolean =>
  !!live(p) && !isInternalLike(p);

export const canSeeDataStorage = (p: RoleProfile): boolean =>
  !isPartnershipTeam(p) &&
  (live(p)?.is_data_storage_user === true ||
    isSuperAdmin(p) ||
    isManager(p) ||
    isRegularInternalUser(p));

export const canSeeIntegrations = (p: RoleProfile): boolean =>
  !isPartnershipTeam(p) &&
  (isSuperAdmin(p) ||
    isManager(p) ||
    live(p)?.is_integration_dashboard_user === true ||
    isRegularInternalUser(p));

export const canSeeAi = (p: RoleProfile): boolean =>
  live(p)?.is_ai_agent_user === true && !isPartnershipTeam(p);

export const canSeeSecureExchange = (p: RoleProfile): boolean =>
  !isPartnershipTeam(p) &&
  (live(p)?.is_secure_file_upload_user === true ||
    isSuperAdmin(p) ||
    isManager(p) ||
    isRegularInternalUser(p));

export const canSeePartnerDirectory = (p: RoleProfile): boolean => !!live(p);

export const canSeeUserManagement = (p: RoleProfile): boolean =>
  !isPartnershipTeam(p) &&
  (isSuperAdmin(p) || isManager(p) || isRegularInternalUser(p));

export const canSeeSftpManagement = (p: RoleProfile): boolean =>
  !isPartnershipTeam(p) && (isSuperAdmin(p) || isManager(p));

export const canSeeBotsManagement = (p: RoleProfile): boolean =>
  isSuperAdmin(p) || isManager(p);

// Bots Management has no read-only tier: whoever may open it may operate it,
// and only Client View makes it read-only (CAL-6047).
export const isBotsOperator = canSeeBotsManagement;

export const canSeePartnerManagement = (p: RoleProfile): boolean =>
  isSuperAdmin(p) || isManager(p) || isPartnershipTeam(p);

// Any internal platform role may open the integration platform (CAL-5832);
// managers get a read-only UI there.
export const canSeeIntegrationsPlatform = (p: RoleProfile): boolean =>
  isDeveloper(p) || isSuperAdmin(p) || isManager(p);

export const canSeeAnyManagement = (p: RoleProfile): boolean =>
  canSeeUserManagement(p) ||
  canSeeSftpManagement(p) ||
  canSeePartnerManagement(p) ||
  canSeeIntegrationsPlatform(p) ||
  canSeeBotsManagement(p);

export const mayDownloadTopFolder = (p: RoleProfile): boolean => {
  const profile = live(p);
  if (!profile) return false;
  if (isSuperAdmin(p) || isManager(p) || profile.user_type === "internal")
    return true;
  return !!(
    profile.is_data_storage_user && profile.can_download_top_document_folders
  );
};

export function roleLabel(p: RoleProfile): string {
  if (!p) return "";
  if (p.is_archived) return "Archived";
  if (isSuperAdmin(p)) return "Super Admin";
  if (isManager(p)) return "Manager";
  if (isPartnershipTeam(p)) return "Partnership Manager";
  if (isRegularInternalUser(p)) return "Internal User";
  return "Client User";
}
